/**
 * Foto-Slot-Modell (reine Funktionen) — leitet aus einer CleaningTaskResponse die
 * Raum-Slots und ihren Belegungszustand ab.
 *
 * Slot = ChecklistItem mit `requiresPhoto`. Belegt = es existiert ein `ChecklistEvidence`-Foto
 * mit `checklistItemId == item.id`. Bei mehreren Fotos pro Slot (Retake-Race) gewinnt das
 * jüngste (`createdAt`) — das ist das Foto, das der Retake-Button löscht.
 */
import type {
  CleaningTaskResponse,
  ChecklistItemResponse,
  CleaningPhotoResponse,
} from '@/shared/api';

export interface PhotoSlot {
  item: ChecklistItemResponse;
  photo: CleaningPhotoResponse | null;
}

/** Latest ChecklistEvidence-Foto pro checklistItemId. */
function latestEvidenceByItem(photos: CleaningPhotoResponse[]): Map<string, CleaningPhotoResponse> {
  const byItem = new Map<string, CleaningPhotoResponse>();
  for (const p of photos) {
    if (p.kind !== 'ChecklistEvidence' || !p.checklistItemId) continue;
    const existing = byItem.get(p.checklistItemId);
    if (!existing || p.createdAt > existing.createdAt) byItem.set(p.checklistItemId, p);
  }
  return byItem;
}

/** Die Foto-Slots der Task in Anzeige-Reihenfolge (nach `position`). */
export function buildPhotoSlots(task: CleaningTaskResponse): PhotoSlot[] {
  const byItem = latestEvidenceByItem(task.photos);
  return task.items
    .filter((i) => i.requiresPhoto)
    .slice()
    .sort((a, b) => a.position - b.position)
    .map((item) => ({ item, photo: byItem.get(item.id) ?? null }));
}

export interface SlotProgress {
  filled: number;
  total: number;
  /** Alle Slots belegt UND es gibt mindestens einen Slot. */
  allFilled: boolean;
}

export function slotProgress(slots: PhotoSlot[]): SlotProgress {
  const total = slots.length;
  const filled = slots.filter((s) => s.photo !== null).length;
  return { filled, total, allFilled: total > 0 && filled === total };
}

/**
 * Task ist in einem Endzustand — es gibt nichts mehr zu tun. `Cancelled` gehoert dazu: die
 * Auto-Complete des Servers steigt bei ihm aus, ein Fortschrittstext waere also ein Versprechen,
 * das niemand einloest. Wer hier einen Zustand vergisst, verspricht dem Reinigungspersonal einen
 * Abschluss, den es durch Fotografieren nie erreicht.
 */
export function isFinalStatus(task: CleaningTaskResponse): boolean {
  return task.status === 'Completed' || task.status === 'Issue' || task.status === 'Cancelled';
}
